'use client';

import { useCallback, useEffect, useRef, useState } from 'react';
import { usePublicUi } from '@/lib/publicUi';
import styles from './InboxBell.module.css';

type InboxLink = { label: string; url: string };
type InboxItem = {
  id: string;
  title: string;
  body: string;
  links: InboxLink[];
  sticky: boolean;
  dismissible: boolean;
  createdAt: string;
  readAt: string | null;
  dismissedAt: string | null;
};

const INBOX_API = 'https://containd.space/v1/webInbox';

function currentIdToken(): string | null {
  if (typeof window === 'undefined') return null;
  return (
    (window as Window & { __CONTAIND_ID_TOKEN__?: string | null })
      .__CONTAIND_ID_TOKEN__ ?? null
  );
}

function formatInboxWhen(iso: string, locale: string): { short: string; full: string } {
  if (!iso) return { short: '', full: '' };
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return { short: '', full: '' };
  const loc = locale === 'jp' ? 'ja' : locale || 'en';
  const abs = Math.abs(Date.now() - d.getTime());
  let short = '';
  try {
    const rtf = new Intl.RelativeTimeFormat(loc, { numeric: 'auto' });
    if (abs < 60 * 1000) short = rtf.format(0, 'second');
    else if (abs < 60 * 60 * 1000) short = rtf.format(-Math.round(abs / 60000), 'minute');
    else if (abs < 24 * 60 * 60 * 1000) short = rtf.format(-Math.round(abs / 3600000), 'hour');
    else if (abs < 7 * 24 * 60 * 60 * 1000) short = rtf.format(-Math.round(abs / 86400000), 'day');
  } catch {
    /* fall through */
  }
  let full = '';
  try {
    full = new Intl.DateTimeFormat(loc, { dateStyle: 'long', timeStyle: 'short' }).format(d);
    if (!short) {
      short = new Intl.DateTimeFormat(loc, { dateStyle: 'medium', timeStyle: 'short' }).format(d);
    }
  } catch {
    full = d.toLocaleString();
    if (!short) short = full;
  }
  return { short, full };
}

function BellIcon() {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true" fill="currentColor">
      <path d="M12 22a2.2 2.2 0 0 0 2.2-2.2h-4.4A2.2 2.2 0 0 0 12 22Zm8-6.2V11a8 8 0 1 0-16 0v4.8L2 18v1h20v-1l-2-2.2Z" />
    </svg>
  );
}

export function InboxBell() {
  const { locale, extra } = usePublicUi();
  const detailsRef = useRef<HTMLDetailsElement>(null);
  const [signedIn, setSignedIn] = useState(false);
  const [items, setItems] = useState<InboxItem[]>([]);
  const [expandedId, setExpandedId] = useState<string | null>(null);

  useEffect(() => {
    const session = (window as Window & {
      ContaindAuthSession?: {
        ensureAuth?: () => Promise<{ uid: string } | null>;
        onAuthChanged?: (fn: (user: { uid: string } | null) => void) => () => void;
      };
    }).ContaindAuthSession;
    const apply = (user: { uid: string } | null) => setSignedIn(Boolean(user));
    void (session?.ensureAuth?.() ?? Promise.resolve(null)).then(apply);
    const unsub = session?.onAuthChanged?.(apply);
    const onAuthChanged = (ev: Event) => {
      const detail = (ev as CustomEvent<{ user?: { uid: string } | null }>).detail;
      apply(detail?.user ?? null);
    };
    document.addEventListener('containd:auth-changed', onAuthChanged);
    return () => {
      unsub?.();
      document.removeEventListener('containd:auth-changed', onAuthChanged);
    };
  }, []);

  const load = useCallback(async () => {
    const token = currentIdToken();
    if (!signedIn || !token) {
      setItems([]);
      return;
    }
    const res = await fetch(`${INBOX_API}?locale=${encodeURIComponent(locale)}`, {
      headers: { Authorization: `Bearer ${token}`, Accept: 'application/json' },
    });
    if (!res.ok) return;
    const data = (await res.json()) as { items?: InboxItem[] };
    const next = Array.isArray(data.items) ? data.items : [];
    setItems(next);
    setExpandedId((cur) => (cur && next.some((i) => i.id === cur) ? cur : null));
  }, [locale, signedIn]);

  useEffect(() => {
    void load().catch(() => undefined);
  }, [load]);

  useEffect(() => {
    const onPointerDown = (ev: PointerEvent) => {
      const el = detailsRef.current;
      if (!el?.open) return;
      if (ev.target instanceof Node && el.contains(ev.target)) return;
      el.open = false;
    };
    document.addEventListener('pointerdown', onPointerDown);
    return () => document.removeEventListener('pointerdown', onPointerDown);
  }, []);

  async function post(id: string, action: 'read' | 'dismiss' | 'delete') {
    const token = currentIdToken();
    if (!token) return;
    await fetch(`${INBOX_API}/${encodeURIComponent(id)}/${action}`, {
      method: 'POST',
      headers: { Authorization: `Bearer ${token}` },
    });
  }

  async function toggleItem(item: InboxItem) {
    const nextId = expandedId === item.id ? null : item.id;
    setExpandedId(nextId);
    if (nextId && !item.readAt) {
      setItems((cur) =>
        cur.map((row) =>
          row.id === item.id ? { ...row, readAt: new Date().toISOString() } : row,
        ),
      );
      void post(item.id, 'read').catch(() => undefined);
    }
  }

  async function dismissItem(id: string) {
    await post(id, 'dismiss').catch(() => undefined);
    setItems((cur) =>
      cur.map((i) =>
        i.id === id ? { ...i, dismissedAt: new Date().toISOString(), readAt: i.readAt || new Date().toISOString() } : i,
      ),
    );
    void load().catch(() => undefined);
  }

  async function deleteItem(id: string) {
    await post(id, 'delete').catch(() => undefined);
    setItems((cur) => cur.filter((i) => i.id !== id));
    setExpandedId((cur) => (cur === id ? null : cur));
    void load().catch(() => undefined);
  }

  if (!signedIn) return null;
  const active = items.filter((i) => !i.dismissedAt);
  const history = items.filter((i) => i.dismissedAt);
  const unread = active.filter((i) => !i.readAt).length;

  function renderItem(item: InboxItem, inHistory: boolean) {
    const open = expandedId === item.id;
    const when = formatInboxWhen(item.createdAt, locale);
    return (
      <article
        key={item.id}
        className={`${styles.item}${!item.readAt && !item.dismissedAt ? ` ${styles.unread}` : ''}${open ? ` ${styles.open}` : ''}`}
      >
        <button
          type="button"
          className={styles.itemHead}
          onClick={() => void toggleItem(item)}
        >
          <span className={styles.itemTitle}>{item.title}</span>
          {when.short ? (
            <time className={styles.itemWhen} dateTime={item.createdAt} title={when.full}>
              {when.short}
            </time>
          ) : null}
        </button>
        {open ? (
          <div className={styles.detail}>
            <p className={styles.modalBody}>{item.body}</p>
            {item.links?.length ? (
              <div className={styles.links}>
                {item.links.map((link) => (
                  <a key={link.url} href={link.url} target="_blank" rel="noopener noreferrer">
                    {link.label || link.url}
                  </a>
                ))}
              </div>
            ) : null}
            {inHistory ? (
              <button
                type="button"
                className={styles.deleteBtn}
                onClick={() => void deleteItem(item.id)}
              >
                {extra.inboxDelete}
              </button>
            ) : item.dismissible ? (
              <button
                type="button"
                className={styles.primary}
                onClick={() => void dismissItem(item.id)}
              >
                {extra.inboxDismiss}
              </button>
            ) : null}
          </div>
        ) : (
          <span className={styles.itemBody}>{item.body}</span>
        )}
      </article>
    );
  }

  return (
    <details ref={detailsRef} className={styles.bell} data-inbox-bell="">
      <summary aria-label={extra.inboxAria} title={extra.inboxAria}>
        <BellIcon />
      </summary>
      {unread > 0 ? (
        <span className={styles.badge}>{unread > 9 ? '9+' : unread}</span>
      ) : null}
      <div className={styles.panel}>
        {items.length === 0 ? (
          <p className={styles.empty}>{extra.inboxEmpty}</p>
        ) : (
          <>
            {active.map((item) => renderItem(item, false))}
            {history.length > 0 ? (
              <>
                <p className={styles.historyLabel}>{extra.inboxHistory}</p>
                {history.map((item) => renderItem(item, true))}
              </>
            ) : null}
          </>
        )}
      </div>
    </details>
  );
}
